// Hero Block Component
// Full-width banner with background image and text overlay
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Upload, Pencil, Trash2, Image as ImageIcon } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../../services/api';

const HeroBlock = ({ content, padding, backgroundColor, gridWidth, gridHeight, onUpdate, onDelete }) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState(content?.backgroundImage || '');
  const [overlayText, setOverlayText] = useState(content?.overlayText || '');
  const [textPlacement, setTextPlacement] = useState(content?.textPlacement || 'middle');
  const [textVertical, setTextVertical] = useState(content?.textVertical || 'middle');
  const [textBgEnabled, setTextBgEnabled] = useState(content?.textBgEnabled || false);
  const [textBgColor, setTextBgColor] = useState(content?.textBgColor || '#000000');
  const [textSize, setTextSize] = useState(content?.textSize || 'medium');
  const [textFont, setTextFont] = useState(content?.textFont || 'system');
  const [editPadding, setEditPadding] = useState(padding || 'none');
  const [editGridWidth, setEditGridWidth] = useState(gridWidth || 12);
  const [editGridHeight, setEditGridHeight] = useState(gridHeight || 3);
  const [uploading, setUploading] = useState(false);

  // Font options for hero text
  const fontOptions = [
    { value: 'system', label: 'System Default' },
    { value: 'Coustard', label: 'Coustard' },
    { value: 'Alfa Slab One', label: 'Alfa Slab One' },
    { value: 'Ovo', label: 'Ovo' },
    { value: 'Fraunces', label: 'Fraunces' },
    { value: 'Lancelot', label: 'Lancelot' },
    { value: 'Arvo', label: 'Arvo' },
    { value: 'Josefin Slab', label: 'Josefin Slab' },
    { value: 'Outfit', label: 'Outfit' },
    { value: 'Cal Sans', label: 'Cal Sans' },
    { value: 'Tilt Warp', label: 'Tilt Warp' },
    { value: 'McLaren', label: 'McLaren' },
    { value: 'Mate', label: 'Mate' },
    { value: 'Buda', label: 'Buda' },
    { value: 'Bellefair', label: 'Bellefair' },
    { value: 'Questrial', label: 'Questrial' },
    { value: 'DM Mono', label: 'DM Mono' },
    { value: 'Quicksand', label: 'Quicksand' },
    { value: 'Nunito Sans', label: 'Nunito Sans' },
    { value: 'Roboto', label: 'Roboto' },
    { value: 'Manrope', label: 'Manrope' },
    { value: 'Jost', label: 'Jost' }
  ];

  const getFontFamily = (fontName) => {
    if (!fontName || fontName === 'system') {
      return 'ui-sans-serif, system-ui, sans-serif';
    }
    return `"${fontName}", sans-serif`;
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.request('/upload/single', {
        method: 'POST',
        body: formData
      });

      const imageUrl = response.file?.url || response.url;
      setBackgroundImage(imageUrl);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = () => {
    onUpdate({
      content: {
        backgroundImage,
        overlayText,
        textPlacement,
        textVertical,
        textBgEnabled,
        textBgColor,
        textSize,
        textFont
      },
      padding: editPadding,
      gridWidth: editGridWidth,
      gridHeight: editGridHeight
    });
    setShowEditModal(false);
  };

  const handleCancel = () => {
    setBackgroundImage(content?.backgroundImage || '');
    setOverlayText(content?.overlayText || '');
    setTextPlacement(content?.textPlacement || 'middle');
    setTextVertical(content?.textVertical || 'middle');
    setTextFont(content?.textFont || 'system');
    setTextBgEnabled(content?.textBgEnabled || false);
    setTextBgColor(content?.textBgColor || '#000000');
    setTextSize(content?.textSize || 'medium');
    setEditPadding(padding || 'none');
    setEditGridWidth(gridWidth || 12);
    setEditGridHeight(gridHeight || 3);
    setShowEditModal(false);
  };

  const openEditModal = () => {
    setBackgroundImage(content?.backgroundImage || '');
    setOverlayText(content?.overlayText || '');
    setTextPlacement(content?.textPlacement || 'middle');
    setTextVertical(content?.textVertical || 'middle');
    setTextBgEnabled(content?.textBgEnabled || false);
    setTextBgColor(content?.textBgColor || '#000000');
    setTextSize(content?.textSize || 'medium');
    setEditPadding(padding || 'none');
    setEditGridWidth(gridWidth || 12);
    setEditGridHeight(gridHeight || 3);
    setShowEditModal(true);
  };

  const getTextPlacementClass = () => {
    let horizontal = '';
    let vertical = '';

    // Horizontal positioning
    switch (textPlacement) {
      case 'left': horizontal = 'justify-start pl-8'; break;
      case 'right': horizontal = 'justify-end pr-8'; break;
      default: horizontal = 'justify-center';
    }

    // Vertical positioning
    switch (content?.textVertical || 'middle') {
      case 'top': vertical = 'items-start pt-8'; break;
      case 'bottom': vertical = 'items-end pb-8'; break;
      default: vertical = 'items-center';
    }

    return `${vertical} ${horizontal}`;
  };

  const getTextSizeStyle = () => {
    switch (content?.textSize) {
      case 'small': return { fontSize: '1.05rem', lineHeight: '1.25' };
      case 'large': return { fontSize: '1.98rem', lineHeight: '1.25' };
      case 'xlarge': return { fontSize: '2.4rem', lineHeight: '1.15' };
      default: return { fontSize: '1.35rem', lineHeight: '1.25' };
    }
  };

  // Render edit modal
  const renderEditModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-100">
            Edit Hero Section
          </h3>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* Background Image Upload */}
          <div>
            <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
              Background Image
            </label>
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
              {backgroundImage ? (
                <div className="space-y-4">
                  <img src={backgroundImage} alt="Hero background" className="max-w-full h-auto max-h-32 mx-auto rounded" />
                  <div className="flex justify-center gap-2">
                    <label className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 text-sm">
                      Change Image
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={uploading}
                      />
                    </label>
                    <button
                      onClick={() => setBackgroundImage('')}
                      className="px-4 py-2 text-red-600 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <label className="cursor-pointer block text-center">
                  <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {uploading ? 'Uploading...' : 'Click to upload background image'}
                  </p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              )}
            </div>
          </div>

          {/* Overlay Text */}
          <div>
            <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
              Overlay Text
            </label>
            {/* Text Size and Font Selectors - inline with editor */}
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600 dark:text-gray-400">Size:</label>
                <select
                  value={textSize}
                  onChange={(e) => setTextSize(e.target.value)}
                  className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
                >
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                  <option value="xlarge">Extra Large</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600 dark:text-gray-400">Font:</label>
                <select
                  value={textFont}
                  onChange={(e) => setTextFont(e.target.value)}
                  className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
                  style={{ fontFamily: getFontFamily(textFont) }}
                >
                  {fontOptions.map(font => (
                    <option key={font.value} value={font.value} style={{ fontFamily: getFontFamily(font.value) }}>
                      {font.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <style>
                {`
                  .hero-editor-${textSize} .ql-editor {
                    font-size: ${textSize === 'small' ? '1.05rem' : textSize === 'large' ? '1.98rem' : textSize === 'xlarge' ? '2.4rem' : '1.35rem'};
                    font-family: ${getFontFamily(textFont)};
                  }
                `}
              </style>
              <ReactQuill
                theme="snow"
                value={overlayText}
                onChange={setOverlayText}
                modules={{
                  toolbar: [
                    ['bold', 'italic', 'underline'],
                    [{ 'align': [] }],
                    ['link'],
                    ['clean']
                  ]
                }}
                placeholder="Enter hero text..."
                className={`bg-white dark:bg-gray-700 hero-editor-${textSize}`}
              />
            </div>
          </div>

          {/* Text Placement */}
          <div>
            <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
              Horizontal Position
            </label>
            <div className="flex gap-3">
              {['left', 'middle', 'right'].map((placement) => (
                <label key={placement} className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    value={placement}
                    checked={textPlacement === placement}
                    onChange={(e) => setTextPlacement(e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">{placement}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Vertical Position */}
          <div>
            <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
              Vertical Position
            </label>
            <div className="flex gap-3">
              {['top', 'middle', 'bottom'].map((position) => (
                <label key={position} className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    value={position}
                    checked={textVertical === position}
                    onChange={(e) => setTextVertical(e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">{position}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Text Background */}
          <div>
            <label className="flex items-center mb-3 cursor-pointer">
              <input
                type="checkbox"
                checked={textBgEnabled}
                onChange={(e) => setTextBgEnabled(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                Enable text box highlight (85% transparent)
              </span>
            </label>

            {textBgEnabled && (
              <div className="ml-7">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Box Background Color
                </label>
                <input
                  type="color"
                  value={textBgColor}
                  onChange={(e) => setTextBgColor(e.target.value)}
                  className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                />
              </div>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 bg-white dark:bg-gray-800 p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button
            onClick={handleCancel}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-[#3C3C3C] dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-[#7A99D8] text-white rounded-lg hover:bg-[#6A89C8]"
          >
            Update Hero
          </button>
        </div>
      </div>
    </div>
  );

  // Empty state
  if (!content?.backgroundImage) {
    return (
      <>
        {/* Hidden edit trigger for PageBuilder */}
        <button
          data-edit-trigger
          onClick={openEditModal}
          className="hidden"
          aria-hidden="true"
        />
        <div
          className="h-full flex items-center justify-center bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600"
        >
          <div className="text-center">
            <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Use edit button to configure hero section</p>
          </div>
        </div>
        {showEditModal && createPortal(renderEditModal(), document.body)}
      </>
    );
  }

  // Display hero
  return (
    <>
      {/* Hidden edit trigger for PageBuilder */}
      <button
        data-edit-trigger
        onClick={openEditModal}
        className="hidden"
        aria-hidden="true"
      />
      <div
        className="h-full relative bg-cover bg-center rounded-b-lg"
        style={{ backgroundImage: `url(${content.backgroundImage})` }}
      >
        {/* Overlay text */}
        <div className={`absolute inset-0 flex ${getTextPlacementClass()}`}>
          {content.overlayText && (
            <div
              className="p-6 rounded-2xl"
              style={{
                backgroundColor: content.textBgEnabled ? `${content.textBgColor}E6` : 'transparent',
                maxWidth: '45%'
              }}
            >
              <style>
                {`
                  .hero-text-content p {
                    margin: 0;
                  }
                  .hero-text-content .ql-align-left {
                    text-align: left;
                  }
                  .hero-text-content .ql-align-center {
                    text-align: center;
                  }
                  .hero-text-content .ql-align-right {
                    text-align: right;
                  }
                  .hero-text-content .ql-align-justify {
                    text-align: justify;
                  }
                `}
              </style>
              <div
                className="text-white hero-text-content"
                style={{
                  ...getTextSizeStyle(),
                  textShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)',
                  fontFamily: getFontFamily(content.textFont)
                }}
                dangerouslySetInnerHTML={{ __html: content.overlayText }}
              />
            </div>
          )}
        </div>

        {/* Edit/Delete buttons - positioned lower to avoid header overlap */}
        <div className="absolute top-12 right-2 flex gap-1">
          <button
            onClick={openEditModal}
            className="p-2 bg-white/90 dark:bg-gray-800/90 border border-gray-300 dark:border-gray-600 rounded shadow-sm hover:bg-white dark:hover:bg-gray-700"
            title="Edit hero"
          >
            <Pencil className="w-4 h-4 text-gray-600 dark:text-gray-300" />
          </button>
          {onDelete && (
            <button
              onClick={onDelete}
              className="p-2 bg-white/90 dark:bg-gray-800/90 border border-gray-300 dark:border-gray-600 rounded shadow-sm hover:bg-red-50 dark:hover:bg-red-900/20"
              title="Delete block"
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </button>
          )}
        </div>
      </div>

      {showEditModal && createPortal(renderEditModal(), document.body)}
    </>
  );
};

export default HeroBlock;
